'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import BannerSection from 'pricing-pages-unified-ui/components/banners/BannerSection';
import CurrencyPicker from 'pricing-pages-unified-ui/components/common/CurrencyPicker';
import FooterMicrocopy from 'pricing-pages-unified-ui/components/common/FooterMicrocopy';
import CustomerSupport from 'pricing-pages-unified-ui/components/common/CustomerSupport';
import HubSpotPayments from 'pricing-pages-unified-ui/components/common/HubSpotPayments';
import Faq from 'pricing-pages-unified-ui/components/common/Faq';
import Question from 'pricing-pages-unified-ui/components/common/Question';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { SCREENS, SUBSCREENS } from '../constants/Tracking';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useAnchorLinkScroll } from '../utils/customHooks';
import { useQueryParamsEffect } from '../utils/queryParamUtils';
import { useQueryParamTerm } from '../utils/stateUtils';
import { HubCalculator } from './common/Calculator';
import TabLayout from './tabs/TabLayout';
import StandardProductPageTitle from './product-pages/StandardProductPageTitle';
import HubSubtitle from './common/HubSubtitle';
import StyledBlob from './common/StyledBlob';
import PageContainer from './common/PageContainer';
import { hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-unified-ui/selectors/LoadingStateSelectors';
import { withDocumentTitle } from 'pricing-pages-unified-ui/utils/DocumentUtils';
import PhoneSupport from 'pricing-pages-unified-ui/components/common/PhoneSupport';
import PhoneSupportFooter from 'pricing-pages-unified-ui/components/common/PhoneSupportFooter';
import FailedLoadBanner from 'pricing-pages-unified-ui/components/loading/FailedLoadBanner';
import FallbackErrorBanner from './loading/FallbackErrorBanner';
import HubTierInfo from './product-pages/HubTierInfo';
import HubInfoContainer from './common/HubInfoContainer';
import PhoneSupportWrapper from './common/phone-numbers/PhoneSupportWrapper';
import CodeSplitEnvWrapper from 'pricing-pages-unified-ui/components/CodeSplitEnvWrapper';
import CalculatorAnchorLink from 'pricing-pages-unified-ui/components/common/CalculatorAnchorLink';
import RecommendedBundlesSection from './product-pages/bundles-section/RecommendedBundlesSection';
import { TabEnum } from '../constants/TabConfig';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getMarketingUrlPublic, getLanguagesUrlPublic, getCommunityUrlPublic, getKnowledgeBaseUrlPublic, getAcademyUrlPublic, getStartupsUrlPublic, getNonprofitsUrlPublic, PAYMENTS_LANDING_PAGE_URL } from 'pricing-pages-lib/utils/publicPageUrls';
import { useMon570 } from '../selectors/ExperimentTreatmentSelectors';
import { useTrackRetail } from '../selectors/RetailSelectors';
import MON570FeatureCardWrapper from './product-pages/MON570FeatureCardWrapper';
import { useFeatureStore } from '../hooks/useFeatureStore';
import { getHighlightsForProductLine } from 'ui-addon-upgrades/_core/features/FeaturesUtils';

function MarketingHubPage({
  hasPricingAndPurchasingSucceeded,
  hasPricingAndPurchasingFailed
}) {
  useAnchorLinkScroll();
  useTrackRetail();
  const {
    isVariant: isMon570Variant
  } = useMon570(MARKETING);
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: SCREENS.PRODUCTS_PLANS,
      subscreen: SUBSCREENS.MARKETING_HUB
    });
  }, []);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const highlightedProducts = getHighlightsForProductLine(ProductLines.MARKETING);
  useFeatureStore({
    highlightedProducts,
    productLine: ProductLines.MARKETING
  });
  return /*#__PURE__*/_jsx(TabLayout, {
    selectedPrimaryTab: TabEnum.PRODUCTS,
    selectedSecondaryTab: TabEnum.MARKETING,
    children: /*#__PURE__*/_jsxs(PageContainer, {
      "data-selenium-id": "page-success",
      children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
        name: "MARKETING_PAGE"
      }), /*#__PURE__*/_jsx(CurrencyPicker, {}), /*#__PURE__*/_jsx(StyledBlob, {}), /*#__PURE__*/_jsxs(HubInfoContainer, {
        children: [/*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsx(StandardProductPageTitle, {
          productLine: ProductLines.MARKETING,
          includeTrademark: true
        }), /*#__PURE__*/_jsxs(HubSubtitle, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricing-pages.hubs.marketing.description"
          }), /*#__PURE__*/_jsx(CalculatorAnchorLink, {})]
        }), /*#__PURE__*/_jsx(BannerSection, {
          productLine: ProductLines.MARKETING,
          screen: SCREENS.PRODUCTS_PLANS,
          subscreen: SUBSCREENS.MARKETING_HUB
        }), /*#__PURE__*/_jsx(HubTierInfo, {
          productLine: MARKETING
        }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import(
          /* webpackChunkName: "public-chunk" */
          'pricing-pages-unified-ui/components/common/FreeCard'),
          publicComponentProps: {
            productLine: ProductLines.MARKETING
          }
        }), isMon570Variant && /*#__PURE__*/_jsx(MON570FeatureCardWrapper, {
          productLine: ProductLines.MARKETING
        }), /*#__PURE__*/_jsx(RecommendedBundlesSection, {
          productLine: ProductLines.MARKETING
        }), /*#__PURE__*/_jsx(PhoneSupportWrapper, {
          children: /*#__PURE__*/_jsx(PhoneSupport, {
            phoneSourceId: "marketing-middle",
            screen: SUBSCREENS.MARKETING_HUB
          })
        }), /*#__PURE__*/_jsx(HubSpotPayments, {}), /*#__PURE__*/_jsx(HubCalculator, {
          productLine: MARKETING,
          screen: SUBSCREENS.MARKETING_HUB
        }), /*#__PURE__*/_jsx(CustomerSupport, {}), /*#__PURE__*/_jsxs(Faq, {
          children: [/*#__PURE__*/_jsx(Question, {
            questionKey: "marketing.whatIsMarketing411",
            href: getMarketingUrlPublic()
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "sales.whatIsPayments411",
            href: PAYMENTS_LANDING_PAGE_URL
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "marketing.whatAreMarketingContacts"
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "marketing.howManyContacts411",
            onClick: () => {
              document.querySelector('#calculator').scrollIntoView({
                behavior: 'smooth'
              });
              getUsageTracker().track('scrollInteraction', {
                name: 'hub-subtitle',
                action: 'clicked calculate your price'
              });
            }
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "marketing.howManyEmails411"
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "marketing.requiredOnboarding411"
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "marketing.whatSupport411",
            passedInOptions: {
              communityHref: getCommunityUrlPublic(),
              academyHref: getAcademyUrlPublic(),
              kbHref: getKnowledgeBaseUrlPublic(),
              langHref: getLanguagesUrlPublic(),
              onLinkClick: url => {
                getUsageTracker().track('externalLinkClick', {
                  name: 'faq-click-link',
                  url
                });
              }
            }
          }), /*#__PURE__*/_jsx(Question, {
            questionKey: "marketing.whatDiscounts411",
            passedInOptions: {
              startupsHref: getStartupsUrlPublic('pricing-marketingpage'),
              nonprofitsHref: getNonprofitsUrlPublic('pricing-marketingpage'),
              onLinkClick: url => {
                getUsageTracker().track('externalLinkClick', {
                  name: 'faq-click-link',
                  url
                });
              }
            }
          }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
        }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: "marketing-footer",
          screen: SUBSCREENS.MARKETING_HUB
        })]
      })]
    })
  });
}

MarketingHubPage.propTypes = {
  hasPricingAndPurchasingSucceeded: PropTypes.bool.isRequired,
  hasPricingAndPurchasingFailed: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
  return {
    hasPricingAndPurchasingSucceeded: hasPricingAndPurchasingSucceededSelector(state),
    hasPricingAndPurchasingFailed: hasPricingAndPurchasingFailedSelector(state)
  };
};

export default connect(mapStateToProps)(withDocumentTitle(MarketingHubPage, 'marketing'));